import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import LangToggle from './LangToggle';
import styles from './Navigation.module.css';

const LINKS = [
  { key: 'nav.work', href: '#work' },
  { key: 'nav.services', href: '#services' },
  { key: 'nav.ai', href: '#ai' },
  { key: 'nav.about', href: '#about' },
  { key: 'nav.faq', href: '#faq' },
  { key: 'nav.contact', href: '#contact' },
];

export default function Navigation() {
  const { t, buildPath } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const burgerRef = useRef(null);
  const drawerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Focus trap + Escape handling while the drawer is open
  useEffect(() => {
    if (!menuOpen) return undefined;

    const drawer = drawerRef.current;
    const getFocusable = () =>
      drawer
        ? Array.from(drawer.querySelectorAll('a[href], button:not([disabled])')).filter(
            (el) => el.offsetParent !== null,
          )
        : [];

    // Move focus into the drawer when it opens
    const focusables = getFocusable();
    if (focusables.length > 0) {
      focusables[0].focus();
    }

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setMenuOpen(false);
        burgerRef.current?.focus();
        return;
      }
      if (e.key === 'Tab' && drawer) {
        const items = getFocusable();
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const close = () => setMenuOpen(false);

  return (
    <>
      <header className={`${styles.nav} ${scrolled ? styles.scrolled : ''} ${menuOpen ? styles.menuActive : ''}`}>
        <div className={`container ${styles.inner}`}>
          <Link to={buildPath('/')} className={styles.logo} onClick={close}>
            EDELNAD
          </Link>

          {/* Desktop nav */}
          <nav className={styles.links} aria-label="Main navigation">
            {LINKS.slice(0, -1).map((link) => (
              <a key={link.href} href={link.href} className={styles.link}>
                {t(link.key)}
              </a>
            ))}
            <LangToggle />
            <a href="#contact" className={styles.cta}>
              {t('nav.cta')}
            </a>
          </nav>

          {/* Burger button — mobile only */}
          <button
            ref={burgerRef}
            className={styles.burger}
            aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            aria-expanded={menuOpen}
            aria-controls="mobile-drawer"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span className={styles.burgerBar} />
            <span className={styles.burgerBar} />
            <span className={styles.burgerBar} />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        id="mobile-drawer"
        ref={drawerRef}
        className={`${styles.drawer} ${menuOpen ? styles.drawerOpen : ''}`}
        aria-hidden={!menuOpen}
      >
        <nav className={styles.drawerLinks} aria-label="Mobile navigation">
          {LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={`${styles.drawerLink} ${link.key === 'nav.contact' ? styles.drawerCta : ''}`}
              onClick={close}
            >
              {t(link.key)}
            </a>
          ))}
          <div className={styles.drawerLang}>
            <LangToggle />
          </div>
        </nav>
      </div>

      {/* Backdrop */}
      {menuOpen && (
        <div className={styles.backdrop} aria-hidden="true" onClick={close} />
      )}
    </>
  );
}
